/**
 * Every call, newest first, as a log rather than a table.
 *
 * GROUPED BY DAY, AND THE DAY IS A CARD. It used to print a date on every row, which is a
 * spreadsheet's answer to "when" - each row saying it independently, none of them saying
 * anything about the shape of the week. A heading per day and a bare time on the row says
 * both at once, and it costs a column, which is what buys room for the sentence in the
 * middle. The day is the BUSINESS's day: a Sydney boat shop's Tuesday, not the reader's.
 *
 * ONE SURFACE PER DAY, AND ONE `<section>` AROUND EACH. This was a single table with the
 * day headings as sticky `<th scope="colgroup">` cells, and the thing the `<tbody>` was
 * doing for free is the thing the section now has to do on purpose: `position: sticky`
 * scopes to the nearest scrolling ancestor's box, so a label pins only for as long as its
 * own parent is on screen. Flat siblings in one container would scope every label to that
 * shared parent - the first day's would pin at the top and NEVER leave, and every later one
 * would stack into the same band and paint over it in DOM order. The section is what makes
 * a day heading un-stick when its day ends.
 *
 * OPENING A CALL DOES NOT LEAVE THIS PAGE. `/calls/:id` is a child route rendered as a
 * dialog over this list - see `pages/CallDialog.tsx` - so the filters, the pages loaded
 * past the first and the scroll position all survive being read. That is also why the row
 * href is built here from the whole query string rather than from `useScopedTo`.
 *
 * The filter state lives in the URL rather than in component state, and that is a
 * deliberate choice with a real payoff: a filtered view is a link. "Look at this" is the
 * most common thing anyone does with a dashboard, and a screen whose state is invisible
 * to the address bar cannot be shared, bookmarked, or reloaded without starting over.
 *
 * THE CONTROLS RIDE IN THE PAGE HEAD, so they pin with the title instead of scrolling away
 * from the list they filter - which on a long day meant scrolling back up to find out why
 * the list was short. `PageHead` publishes its own measured height as `--spacing-head`, so
 * the day labels below clear whatever the bar turns out to be.
 *
 * AND THE LEDE WENT WITH THEM. A sentence, a search box, four filters, a toggle and a chip
 * row is a lot of pinned chrome for a screen whose job is the rows underneath. The lede is
 * for a title that cannot carry its own meaning; the filter bar now says what this page is
 * more precisely than the sentence did.
 *
 * THE ACTIVE FILTERS ARE ALSO DRAWN AS REMOVABLE CHIPS. A rank of dropdowns each showing
 * its own value is a bar you have to read left to right to find out what you look at; a
 * row of chips is a sentence - "hot, booked, handled end to end" - with an x on each. It
 * is also the only affordance that makes "why is this list so short" answerable at a
 * glance, which is the question a filtered table gets asked most.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Outlet, useOutletContext, useSearchParams } from "react-router-dom";
import { api, useApi, type Band, type CallSummary } from "../api";
import { QualityInline, bandWord } from "../components/Qualification";
import { PAGE_WIDTH, PageHead } from "../components/Page";
import { Empty, SkeletonRows } from "../components/States";
import { useScopedTo } from "../components/Shell";
import { useAgent } from "../currentAgent";
import { Filter, Phone, Search, User } from "../components/Icons";
import { ClipSquare } from "../components/RecordingPlayer";
import { SimpleCalls } from "./SimpleCalls";
import {
  Basis,
  Chip,
  Flag,
  RemovableChip,
  Select,
  TextInput,
  Toggle,
  cx,
} from "../ui";
import {
  basisLabel,
  dayKey,
  dayLabel,
  duration,
  handle,
  humanise,
  labelFor,
  money,
  timeOfDay,
} from "../format";

interface Listed {
  calls: CallSummary[];
  cursor: string | null;
}

/**
 * What this page hands the call dialog underneath it.
 *
 * The dialog is a CHILD ROUTE, so it renders inside this component and reads the list it
 * was opened from rather than fetching one of its own. That is what makes its prev/next
 * arrows possible at all: "the next call" is a position in the rows on screen, filters and
 * all, and no endpoint knows what those are.
 */
export interface CallListContext {
  calls: CallSummary[];
  /** A link to one call that keeps every filter in the address bar. See `hrefFor` below. */
  hrefFor: (id: number) => string;
  /** Back to the list, same filters. */
  listHref: string;
  /** Fetch the next page. Resolves with what it appended - empty when there was no more. */
  loadMore: () => Promise<CallSummary[]>;
  hasMore: boolean;
  /**
   * Put a newer copy of one call in place of the row on screen - a call somebody just
   * marked handled in the dialog over it. The list is not refetched: that would drop the
   * pages loaded past the first, and the row the reader came from would jump.
   */
  replace: (call: CallSummary) => void;
}

export function useCallList(): CallListContext {
  return useOutletContext<CallListContext>();
}

/**
 * The pages of calls behind one query string, and everything a child route needs to step
 * through them.
 *
 * SHARED BY THE LOG AND THE SIMPLIFIED REGISTER, which draw the same rows two different
 * ways. Written into each page it would be two copies of the paging, two of the reset on a
 * filter change and two of the href rule below - and the first time one of them forgot the
 * reset, one view would show rows the other had correctly dropped.
 */
export function useCallPages(query: string) {
  const agent = useAgent();
  // The page's own query is the filters; the agent is the address's, and is named to the
  // Worker by id.
  const listed = `/v1/calls?agent=${encodeURIComponent(agent.id)}${query ? `&${query}` : ""}`;
  const { data, error, loading } = useApi<Listed>(listed);
  const [extra, setExtra] = useState<CallSummary[]>([]);
  /*
   * UNDEFINED UNTIL A SECOND PAGE HAS BEEN ASKED FOR, and that is not the same as null. Null
   * is the answer "there are no more"; undefined is "nothing past the first page yet, so the
   * first page's own cursor is the next one". Folding the two together with `??` made the
   * end of the list fall back to the FIRST page's cursor, and "Load more" fetched page two a
   * second time and printed every row on it twice.
   */
  const [cursor, setCursor] = useState<string | null | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  /** Rows changed on this screen since the list was fetched. See `replace` on the context. */
  const [patched, setPatched] = useState<ReadonlyMap<number, CallSummary>>(() => new Map());

  // A new filter starts a new list. Without this the "load more" pages accumulate across
  // filter changes and the table shows rows that do not match what the filters say - and a
  // row patched under the old filter would be laid over a list it may not belong to.
  useEffect(() => {
    setExtra([]);
    setCursor(undefined);
    setPatched(new Map());
  }, [query]);

  const rows = useMemo(() => {
    const all = data ? [...data.calls, ...extra] : extra;
    if (!patched.size) return all;
    return all.map((call) => {
      const newer = patched.get(call.id);
      /*
       * THE ROW'S OWN KEYS, WITH THE NEWER VALUES. What comes back from a write is the whole
       * detail - transcript, timeline and the contact's number among it - and laying that
       * over a list row changed what the row DREW: a call the register titled "Caller B97E"
       * was retitled by the number only the detail carries, the moment somebody marked it.
       * A summary-shaped copy keeps the row saying what it said before, plus the mark.
       */
      if (!newer) return call;
      const merged = { ...call };
      for (const key of Object.keys(call) as Array<keyof CallSummary>) {
        (merged as Record<string, unknown>)[key] = newer[key];
      }
      return merged;
    });
  }, [data, extra, patched]);
  const nextCursor = cursor === undefined ? (data?.cursor ?? null) : cursor;

  /*
   * THE WHOLE QUERY STRING, NOT `useScopedTo`'s TWO KEYS - and this is load-bearing rather
   * than tidy. The dialog is a child route, so it renders without unmounting this page and
   * without refetching the list, but only while the SEARCH is identical: `useApi` is keyed
   * on `/v1/calls?${query}`. Opening a call with `to("/calls/412")` would drop `band=hot`
   * from the address bar, change that key, refetch the list, clear the pages loaded past
   * the first, and land the reader on an unfiltered list when they closed the dialog.
   */
  const hrefFor = (callId: number) => `/${agent.slug}/calls/${callId}${query ? `?${query}` : ""}`;
  const listHref = `/${agent.slug}/calls${query ? `?${query}` : ""}`;

  /*
   * Returns what it appended, which is what lets the dialog's "next" arrow page the list
   * and then step onto the first new row: after the await its own `calls` is the array
   * from before it. The guard is not decoration either - the arrow and the button can now
   * both reach this.
   */
  async function loadMore(): Promise<CallSummary[]> {
    if (!nextCursor || loadingMore) return [];
    setLoadingMore(true);
    try {
      const page = await api<Listed>(`${listed}&cursor=${encodeURIComponent(nextCursor)}`);
      setExtra((current) => [...current, ...page.calls]);
      setCursor(page.cursor);
      return page.calls;
    } finally {
      setLoadingMore(false);
    }
  }

  const replace = useCallback((call: CallSummary) => {
    setPatched((previous) => new Map(previous).set(call.id, call));
  }, []);

  const context: CallListContext = {
    calls: rows,
    hrefFor,
    listHref,
    loadMore,
    hasMore: Boolean(nextCursor),
    replace,
  };

  return { rows, error, loading, loadingMore, nextCursor, loadMore, context };
}

interface LabelRow {
  label: string;
  group: string | null;
  calls: number;
}

const HANDOFFS = ["booked", "callback", "sms", "referral", "escalation", "none"];

/**
 * The seven bands, in the order they are worth reading.
 *
 * All seven are offered rather than the four temperatures: a filter that cannot reach a
 * value the table prints is a filter the reader cannot verify, and "show me the wrong
 * numbers" is a real thing to want to check once.
 */
const BANDS = [
  "hot", "warm", "cool", "cold", "unscorable", "not_a_fit", "not_an_enquiry",
] as const;

/**
 * Which query keys are filters, and what a chip should call each one.
 *
 * `agent` IS NOT ONE OF THEM ANY MORE. It is the current agent - carried across every
 * navigation by `useScopedTo`, chosen in the top bar, and the thing this whole page is about -
 * so a removable chip reading "agent Eco Boats" over a table would offer to clear the
 * current agent from inside the filter bar, which is the difference between a switcher and a
 * filter drawn as if it were a filter.
 */
const CHIP_LABELS: Record<string, string> = {
  q: "matching",
  band: "quality",
  capture: "",
  label: "outcome",
  // What the READING said the caller wanted, which is a different column and a different
  // sentence from `label` - the grade the agent sent at the end of the call. The chip reads
  // "rang about Repower" so the two can never be confused on screen.
  intent: "rang about",
  // The other half of the reading, and the chip says the verb so it cannot be read as an
  // outcome: "sounded Negative", never a bare "Negative" beside "outcome Booked".
  sentiment: "sounded",
  handoff: "handoff",
  resolved: "",
  // A PERSON'S MARK, not the agent's grade beside it. Spelled out in `chipText`, because a
  // humanised "true" is not a chip anybody can read.
  open: "",
  // One contact's calls, which is where a call opened from the Contacts page lands. The id
  // is all the URL has, so the chip says what the filter is rather than whose it is; the
  // dialog over it names them.
  contact: "",
};

/**
 * `/calls`, drawn the way this business reads it.
 *
 * A SETTING ON THE BUSINESS, NOT A PARAMETER IN THE ADDRESS BAR. The simplified register is
 * for a tablet on a counter that nobody configures twice, so it is chosen once on /settings
 * and every link into /calls - the overview's tiles, an email, the back button - lands in
 * it. Both views are the same route with the same child, so `/calls/412` opens call 412 in
 * either.
 *
 * The view is the agent's, and the agent is known before this renders - `AgentScope` waits for
 * the list - so a counter tablet never flashes the log's filter bar on its way to the register.
 */
export function Calls() {
  return useAgent().view === "simple" ? <SimpleCalls /> : <CallsLog />;
}

function CallsLog() {
  const [params, setParams] = useSearchParams();
  const to = useScopedTo();

  // The search box is debounced separately from the URL, so typing does not push a
  // history entry per keystroke and the back button remains usable.
  const [search, setSearch] = useState(params.get("q") ?? "");
  useEffect(() => {
    const timer = setTimeout(() => {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          if (search) next.set("q", search);
          else next.delete("q");
          return next;
        },
        { replace: true },
      );
    }, 250);
    return () => clearTimeout(timer);
  }, [search, setParams]);

  const query = params.toString();
  const { rows, error, loading, loadingMore, nextCursor, loadMore, context } = useCallPages(query);
  const current = useAgent();
  // Asked for separately rather than derived from the rows on screen: filtering to
  // "hot" would otherwise leave "hot" as the only option in the dropdown. Carries the
  // agent and the test-calls flag, so the dropdown and the table always agree about
  // which population is on screen.
  const { data: labelData } = useApi<{ labels: LabelRow[] }>(
    `/v1/calls/labels?test=${params.get("test") === "true"}&agent=${encodeURIComponent(current.id)}`,
  );
  // The current agent's words, not a second copy fetched beside the labels. Without them this
  // page says "hot" where the overview says "Hot lead", and one of the two goes stale the
  // moment somebody renames a label.
  const words = current.vocabulary.labels;
  const tz = current.timezone;
  // What a call's own currency falls back to. From the current agent rather than a constant:
  // the same figure is dollars in Sydney and pounds in London.
  const currency = current.currency;

  const set = useCallback(
    (key: string, value: string) => {
      setParams((previous) => {
        const next = new URLSearchParams(previous);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      });
    },
    [setParams],
  );

  /*
   * Named rather than inline, because it now has TWO callers: the "Clear all" link in the
   * chip row, and the way out of the "nothing matches these filters" empty state. Two
   * copies of this loop is two chances for one of them to forget a key that was added to
   * `CHIP_LABELS` later.
   */
  const clearAll = useCallback(() => {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      for (const key of Object.keys(CHIP_LABELS)) next.delete(key);
      return next;
    });
  }, [setParams]);

  const active = Object.keys(CHIP_LABELS).filter((key) => params.get(key));
  const testing = params.get("test") === "true";

  /*
   * WHICH ROW'S RECORDING IS PLAYING, held here and nowhere else.
   *
   * Fifty calls talking over each other would make the log useless, and the state has to
   * live above the rows for any of them to be able to silence the others. The rows do the
   * silencing themselves - see `ClipSquare` - so this is one number rather than a ref to
   * every audio element on the page.
   */
  const [playing, setPlaying] = useState<number | null>(null);

  /*
   * ONE GROUP PER DAY, in the business's clock. `rows` is newest-first and pages append in
   * order, so a Map in insertion order is both the right sequence and the thing that merges
   * a day split across a page boundary - the common case, since a busy day is longer than
   * one page.
   */
  const groups = useMemo(() => {
    const map = new Map<string, CallSummary[]>();
    for (const call of rows) {
      const key = dayKey(call.startedAt, tz);
      let bucket = map.get(key);
      if (!bucket) map.set(key, (bucket = []));
      bucket.push(call);
    }
    return [...map].map(([key, calls]) => ({
      key,
      label: dayLabel(calls[0]!.startedAt, tz),
      calls,
    }));
  }, [rows, tz]);

  return (
    <>
      <PageHead
        title="Calls"
        contain={PAGE_WIDTH}
        actions={
          <Toggle checked={testing} onChange={(on) => set("test", on ? "true" : "")}>
            Show test calls
          </Toggle>
        }
        below={
          /* THE CONTROLS, INSIDE THE BAR THAT PINS. See this file's header for why they
             moved. Everything here is one row that wraps; nothing is positioned against
             anything else, so three rows on a narrow window is a taller bar and not a
             broken one - and `PageHead` measures whichever it turns out to be. */
          <div className="flex flex-wrap items-center gap-2">
            <label className="relative flex items-center">
              <Search size={17} className="pointer-events-none absolute left-3 text-ink-ghost" />
              <TextInput
                pill
                type="search"
                placeholder="Name, headline or number"
                aria-label="Search calls"
                className="min-w-65 pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>

            {/* A RULE, NOT A GAP. The box on the left searches; the four on the right
                narrow. Without something between them the set reads as five controls of
                one kind and the eye has to try each to find out. */}
            <span aria-hidden="true" className="mx-1 h-5.5 w-px flex-none bg-line" />

            {/* FIRST AMONG THE DROPDOWNS, because it is the column the log leads with after
                the caller. `capture` gets no dropdown of its own: the bar is already long,
                and the one view that matters - strong enquiries with no way back to them -
                is reached by a link from the overview rather than by assembling two
                controls. It still draws a removable chip, and `clearAll` still clears it,
                because both read `CHIP_LABELS`. */}
            <Select
              pill
              set={Boolean(params.get("band"))}
              value={params.get("band") ?? ""}
              aria-label="Lead quality"
              onChange={(e) => set("band", e.target.value)}
            >
              <option value="">Any quality</option>
              {BANDS.map((band) => (
                <option key={band} value={band}>
                  {bandWord(words, band)}
                </option>
              ))}
            </Select>

            {/* The labels offered are the ones this database actually holds rather than a
                hard-coded list: the same build serves a boat shop and a dentist, and
                neither one's vocabulary belongs in the source. */}
            <Select
              pill
              set={Boolean(params.get("label"))}
              value={params.get("label") ?? ""}
              aria-label="Outcome"
              onChange={(e) => set("label", e.target.value)}
            >
              <option value="">Any outcome</option>
              {(labelData?.labels ?? []).map((row) => (
                <option key={row.label} value={row.label}>
                  {humanise(row.label)} ({row.calls})
                </option>
              ))}
            </Select>

            <Select
              pill
              set={Boolean(params.get("handoff"))}
              value={params.get("handoff") ?? ""}
              aria-label="Handoff"
              onChange={(e) => set("handoff", e.target.value)}
            >
              <option value="">Any handoff</option>
              {HANDOFFS.map((handoff) => (
                <option key={handoff} value={handoff}>
                  {humanise(handoff)}
                </option>
              ))}
            </Select>

            <Select
              pill
              set={Boolean(params.get("resolved"))}
              value={params.get("resolved") ?? ""}
              aria-label="Handled end to end"
              onChange={(e) => set("resolved", e.target.value)}
            >
              <option value="">Handled or not</option>
              <option value="true">Handled end to end</option>
              <option value="false">Needed someone</option>
            </Select>

            {/* WHAT A PERSON HAS DONE ABOUT IT, beside what the agent said about it. The
                one to the left is the agent's own grade and never changes; this one moves
                the moment somebody marks a call handled, and "waiting on you" is exactly
                the list the overview's attention tile opens. */}
            <Select
              pill
              set={Boolean(params.get("open"))}
              value={params.get("open") ?? ""}
              aria-label="Waiting on you"
              onChange={(e) => set("open", e.target.value)}
            >
              <option value="">Open or handled</option>
              <option value="true">Waiting on you</option>
              <option value="false">Handled by someone</option>
            </Select>

            {active.length > 0 && (
              <button
                className="cursor-pointer px-1 text-sm font-medium text-accent hover:underline"
                onClick={clearAll}
              >
                Clear
              </button>
            )}

            {/*
              THE COUNT, AND IT IS NOT "3 of 8". There is no denominator to print: the
              endpoint returns a page and a cursor and no total, so the only honest
              statement is how many are on screen and whether that is all of them. "so far"
              is what carries that, and it is the same sentence the footer used to make at
              the bottom of the page where nobody scrolled to read it.
            */}
            {rows.length > 0 && (
              <span className="nums ml-auto pl-2 text-sm whitespace-nowrap text-ink-faint">
                {rows.length} call{rows.length === 1 ? "" : "s"}
                {nextCursor ? " so far" : ""}
              </span>
            )}
          </div>
        }
      />

      <div className={PAGE_WIDTH}>
        {active.length > 0 && (
          <div className="mb-3.5 flex flex-wrap items-center gap-1.5">
            {active.map((key) => (
              <RemovableChip key={key} onRemove={() => set(key, "")}>
                {chipText(key, params.get(key)!, words)}
              </RemovableChip>
            ))}
            <button
              className="cursor-pointer text-sm font-medium text-accent hover:underline"
              onClick={clearAll}
            >
              Clear all
            </button>
          </div>
        )}

        {params.get("intent") === "other" && (
          /* THE ONE FILTERED VIEW THAT NEEDS A SENTENCE. Every other chip narrows to a
             category somebody chose; this one narrows to the calls that fitted NONE of them,
             which is not a category and reads as one. Saying what the bucket is, next to the
             one action that shrinks it, is what turns the biggest bar on the overview into
             something an owner can do anything about. */
          <p className="mb-3.5 text-sm text-ink-soft">
            These calls did not fit any category.{" "}
            <Link className="font-medium text-accent hover:underline" to={to("/settings?tab=tracking")}>
              Add one in Settings
            </Link>
            .
          </p>
        )}

        {error && (
          /* A 401 IS NOT AN ALERT. It is reachable by doing nothing at all - leaving this
             tab open until the cookie expires - so the reader did nothing wrong and gets no
             red triangle. "Not signed in" also stated a fact and offered nothing; a reload
             drops through the gate in App.tsx and lands on the sign-in card. */
          <Empty
            tone={error.status === 401 ? undefined : "alert"}
            icon={error.status === 401 ? User : undefined}
            title={error.status === 401 ? "Your session has expired" : "Couldn't load calls"}
            body={
              error.status === 401
                ? "You were signed out while this page was open."
                : error.message
            }
            action={
              error.status === 401
                ? { label: "Sign in again", onClick: () => window.location.reload() }
                : undefined
            }
          />
        )}

        {loading && !rows.length && (
          <div className="mt-2">
            <SkeletonRows rows={10} />
          </div>
        )}

        {!error && !loading && !rows.length && (
          <Empty
            icon={active.length ? Filter : Phone}
            title={active.length ? "Nothing matches these filters" : "No calls yet"}
            body={
              active.length
                ? "Loosen one of the filters above, or clear them all."
                : "Calls appear here within a second of the caller hanging up."
            }
            /*
              UNCONDITIONAL NOW. This used to render only when `!testing`, so a reader who
              had already turned test calls on and still saw nothing got a title, a sentence
              and no way forward - the exact dead end this component's own doc-comment names
              as the thing it exists to prevent, in the file's own example sentence.
              The `testing` string is not a way out and is not pretending to be one; it
              closes the only doubt left worth closing, which is "is this hiding something
              from me". Without a lifetime-calls signal from the API that is the most that
              can honestly be said here.
            */
            hint={
              testing
                ? "Test and demo calls are included in this list, so nothing is being hidden."
                : "Test and demo calls are hidden. Turn on “Show test calls” above to include them."
            }
            /* The hint above told the reader to clear the filters and gave them nothing to
               click. This is that button - and it is the same handler as the chip row's. */
            action={active.length ? { label: "Clear all filters", onClick: clearAll } : undefined}
          />
        )}

        <div className="grid gap-4.5">
          {groups.map((group) => (
            /*
              ONE SECTION PER DAY, and the nesting is load-bearing rather than tidy - see
              this file's header. The label sticks INSIDE this element and therefore only
              while this day is on screen; the card below it is a separate box, which is
              what lets the card keep `overflow-hidden` without killing the sticky.
            */
            <section key={group.key} className="grid gap-2">
              <div
                className={cx(
                  /* BELOW THE PAGE HEAD, NOT AT THE TOP OF THE VIEWPORT. The shell's top
                     bar and `PageHead` are both sticky on every screen, so `top-0` would pin
                     these behind the two of them and they would simply never be seen. Both
                     heights are measured and published by their own components, so the sum
                     cannot drift and this does not have to guess at a filter bar that wraps
                     to three rows on one width and two on another.

                     A BRACKET WITH `var()` SPELLED OUT, because this one is a `calc`. The
                     trap is the bare bracket, `top-[--var]`, which emits `top: --spacing-head`
                     - not a value, so the declaration is dropped and the heading silently
                     stops sticking. `top-(--var)` is the shorthand for ONE variable; a sum of
                     two has to be written as the `calc` it is. */
                  "sticky top-[calc(var(--spacing-bar)+var(--spacing-head))] z-10 flex items-baseline gap-2.5",
                  /* GROUND, NOT PAPER. It sits in the gap BETWEEN two white cards, and a
                     white bar in that gap is visibly the wrong colour; transparent lets the
                     rows show through it as they scroll under. The negative margins and the
                     padding are what give it a band tall enough to occlude them. */
                  "-mx-2 bg-ground px-2 pt-1 pb-1.5",
                  "text-2xs font-semibold tracking-[0.06em] text-ink-ghost uppercase",
                )}
              >
                {group.label}
                <span className="nums font-mono text-2xs font-normal tracking-normal text-ink-faint">
                  {group.calls.length}
                </span>
              </div>

              {/* `overflow-hidden` BELONGS HERE AND NOWHERE ABOVE IT. It clips the first and
                  last row's hover wash to the radius, which is the whole reason the rows sit
                  in a box. On the `<section>` it would clip the sticky label out of
                  existence - which is what the old single-table version of this file spent a
                  paragraph warning about, from the other side of the same rule. */}
              <div className="overflow-hidden rounded-lg border border-line bg-paper">
                {group.calls.map((call, index) => (
                  <Row
                    key={call.id}
                    call={call}
                    href={context.hrefFor(call.id)}
                    words={words}
                    tz={tz}
                    currency={currency}
                    first={index === 0}
                    playing={playing}
                    onPlay={setPlaying}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>

        {nextCursor && (
          <button
            onClick={() => void loadMore()}
            disabled={loadingMore}
            className="mt-4.5 w-full cursor-pointer rounded-sm border border-line bg-paper p-2.75 text-sm font-medium text-ink-soft hover:border-line-strong hover:text-ink disabled:opacity-55"
          >
            {loadingMore ? "Loading…" : "Load more calls"}
          </button>
        )}
      </div>

      {/* The call dialog, when the URL names one. It portals itself to the body, so where
          it sits in this tree is cosmetic - what matters is that it renders INSIDE this
          component, which is what keeps the list and its loaded pages alive underneath. */}
      <Outlet context={context} />
    </>
  );
}

function chipText(key: string, value: string, words: Record<string, string>): string {
  if (key === "contact") return "one contact's calls";
  if (key === "resolved") return value === "true" ? "Handled end to end" : "Needed someone";
  if (key === "open") return value === "true" ? "waiting on you" : "handled by someone";
  // The business's own word for the category, the same one the overview's bar was labelled
  // with. `humanise` would render the raw key and the chip would disagree with the chart it
  // was clicked from.
  if (key === "intent") return `${CHIP_LABELS.intent} ${labelFor(words, value)}`;
  // `band` arrives as a comma list from the overview's link, and as one value from the
  // dropdown. Both read as a sentence: "quality Hot or Warm".
  if (key === "band") {
    return `quality ${value.split(",").map((band) => bandWord(words, band as Band)).join(" or ")}`;
  }
  // Spelled out rather than humanised. A chip reading "None" over a filtered table is a
  // word with no subject; this one says what was filtered on.
  if (key === "capture") {
    return value === "none" ? "nothing captured" : `captured ${humanise(value)}`;
  }
  return `${CHIP_LABELS[key]} ${humanise(value)}`.trim();
}

/**
 * One call, on one line - and the line is a link.
 *
 * IT IS A REAL `<Link>` AND NOT A CLICKABLE `<tr>`. The row used to be a table row with an
 * `onClick` that called `navigate`, plus a hand-rolled `querySelector("a")?.focus()` so
 * that closing the dialog would return focus to the row rather than to `<body>`. A link
 * needs none of that: the browser keeps focus on the thing that was clicked, `Dialog`
 * hands it back on close, and middle-click, Cmd-click and "open in new tab" work because
 * they are not being simulated. It is still a router `Link` rather than a bare `<a>` -
 * a document navigation would throw away the list this dialog opens on top of.
 *
 * SIX CELLS, AND THE ORDER IS HOW A LOG IS READ: who rang, for how long, how good the
 * enquiry was, what happened, what it was worth, and where it stands now - open, handed
 * off, or handled by somebody. The day is the heading above, so the time rides under the
 * caller rather than holding a column.
 *
 * WHAT HAPPENED GETS TWO LINES: the agent's headline, and under it the reading's `ask` -
 * what the caller wanted, as a label under fifty characters. The second line used to be
 * the summary, a sentence written for the call itself that this column cut off after its
 * opening "The caller reported that…", so the one specific thing in it was never on the
 * row. The label fits, and it says what a headline like "AFX-2609 - enquiry" does not.
 * Nothing rides in front of it: the location that used to lead this line is on the call.
 *
 * THE MONEY AND THE HANDOFF HOLD COLUMNS, and that reverses what this comment used to
 * argue. They rode the end of the sentence when the sentence was one line and they were
 * the only things short enough to sit beside it. With the sentence on two lines they had
 * nowhere to ride, and a figure that lands at a different x on every row cannot be
 * compared down the column - which is the whole reason to print money in a log.
 *
 * ONE GRADE PER ROW, AND IT IS THE METER. The agent's own outcome badge used to sit one
 * cell over, and on a call the agent had labelled "warm" the row printed the word twice in
 * two different voices, sometimes disagreeing - a green "hot" badge beside the word "Warm".
 * Two grades side by side is not twice the information; it is a reader working out which
 * one to believe. The one that survives is the four-step meter, because it is COMPARABLE:
 * it is the same 0-100 scale on every row of every agent, where the badge is whatever
 * noun that agent's author happened to send. The label is still on the call itself, and
 * still filterable from the bar above.
 *
 * THE ICON IS ALWAYS A HANDSET. `channel` is `phone | browser | eval`, and a globe in the
 * first column made a column out of a distinction almost no row draws, and read as a
 * status rather than as a medium. The first cell carries the call's HANDLE rather than a
 * name for the same reason it no longer says "Web call": every demo call is a browser call
 * with no number, so a name-or-channel title was a column of identical labels the moment
 * the name went away.
 */
// Exported for `Calls.test.tsx`, which renders one row and asserts it is titled by the
// call's handle and never by a contact name. Nothing else imports it.
export function Row({
  call,
  href,
  words,
  tz,
  currency,
  first,
  playing = null,
  onPlay = () => {},
}: {
  call: CallSummary;
  /** Built by the page, and carrying the whole query string. See `hrefFor`. */
  href: string;
  words: Record<string, string>;
  /** The business's clock. Undefined falls back to the reader's browser. */
  tz: string | null | undefined;
  /** What a value with no currency of its own is in. From the current agent, not a constant. */
  currency: string | null | undefined;
  /** The rule goes on top of every row but the first, so the card's edge stays its own. */
  first?: boolean;
  /**
   * Which row's recording is the live one, held by the page rather than by the rows.
   *
   * Fifty calls playing over each other is the same problem the voice picker's forty rows
   * had, and it has the same answer: one piece of state above them all, and every row that
   * is not it stops itself. The defaults make a row usable on its own - which is how
   * `Calls.test.tsx` renders one - at the cost of nothing but the coordination.
   */
  playing?: number | null;
  onPlay?: (id: number) => void;
}) {
  const outcome = call.outcome;
  // Printed once each and never twice: the headline is the agent's own line about the call
  // and `ask` is the analyser's, so when there is a headline the ask trails it.
  const head = outcome.headline ?? call.analysis.ask;
  const tail = outcome.headline ? call.analysis.ask : null;

  return (
    <div
      className={cx(
        /*
         * A GRID WITH A STRETCHED LINK IN IT, RATHER THAN A LINK THAT IS THE GRID. The row
         * was the `<Link>` itself until a call could be played from it, and a `<button>`
         * inside an `<a>` is not something HTML allows or browsers agree about. The anchor
         * is still a real router `Link` and still the whole row - it is absolutely
         * positioned over it, so it takes every click the play button does not, keeps its
         * focus ring around the whole row, and middle-click, Cmd-click and "open in new
         * tab" go on working because they are not being simulated.
         */
        "relative",
        /*
         * THE FIXED TRACKS ADD UP TO MORE THAN A PHONE IS WIDE, which is why there are two
         * templates and not one with a `minmax`. At 375px the `1fr` column would be handed
         * zero and the six fixed ones would overflow - so the log would drop the only thing
         * on the row anybody reads and keep the duration. Under 720px the sentence takes
         * the full width and everything that qualifies it wraps underneath.
         *
         * A WINDOW QUERY AND NOT `@container`: this list has no container-query ancestor,
         * and adding one here would put a third measuring regime on a page that already
         * has the window for the shell and a container inside the dialog.
         */
        "grid grid-cols-[150px_64px_118px_minmax(0,1fr)_110px_136px] items-center gap-x-4 px-4 py-2.5",
        /*
         * NARROW IS THREE ROWS AND NOTHING IS DROPPED. An earlier pass hid the duration
         * and the quality meter under 720px, which took the GRADE off the row - the one
         * thing somebody scanning a phone is scanning for - and kept the duration, which is
         * the one thing nobody sorts by. Three tracks is enough to give all six cells a
         * home: who and when, then the sentence across the width, then the grade with the
         * money against the right edge.
         */
        "max-[720px]:grid-cols-[minmax(0,1fr)_auto_auto] max-[720px]:gap-y-1.5 max-[720px]:py-3",
        "text-ink hover:bg-sunken",
        !first && "border-t border-line-soft",
      )}
    >
      {/* Named rather than titled by its contents: with the anchor stretched over the row
          its own text children are gone, and "Caller 9702" is a better link name than the
          whole row read aloud was. */}
      <Link to={href} aria-label={`Open call ${handle(call.callId)}`} className="absolute inset-0" />

      <span className="flex min-w-0 items-center gap-2">
        {/*
          THE PLAY BUTTON TAKES THE HANDSET'S SLOT, and both are 32px so the handle beside
          them starts at the same x on every row - a column of names that shifts by row is
          the thing this file's other notes keep arguing against.

          IT IS ALSO THE ONLY CONTROL ON THE ROW, which is what `z-10` is for: it paints
          above the stretched link, so pressing it plays the call instead of opening it.
          Rows with no recording keep the glyph, which is the honest difference - one of
          these calls can be heard and the other cannot.
        */}
        {call.recording ? (
          <span className="relative z-10 flex-none">
            <ClipSquare
              variant="quiet"
              path={`/v1/calls/${call.id}/recording?v=${call.recording.sha256}`}
              label={`Play the recording of ${handle(call.callId)}`}
              active={playing === call.id}
              onPlay={() => onPlay(call.id)}
            />
          </span>
        ) : (
          <span className="grid size-8 flex-none place-items-center">
            <Phone size={15} className="text-ink-ghost" />
          </span>
        )}
        <span className="grid min-w-0">
          {/*
            ONE TEXT NODE, AND THAT IS A TEST CONTRACT AS WELL AS A DESIGN ONE. The handle,
            and nothing else. A name is the one field on a call that cannot be derived - it
            is only ever what the caller said - and titling a row with it put an agent's
            invention at the top of the log. When Twilio lands and `caller` is a real
            number, `phone(call.caller) ?? handle(...)` is the one-line change that puts the
            number back in front. Do not split it into "Caller " plus a code: `getByText`
            reads a node's own text children, and four tests look for the whole string.
          */}
          <span className="truncate text-sm font-medium">{handle(call.callId)}</span>
          {/* THE MODE RIDES THE TIME LINE, not the name. Beside the handle it competed for
              a 150px track and won - "Caller C08A" truncated to "Caller C…" so that the
              word "demo" could be complete, which is precisely backwards. Underneath, the
              two things that qualify the call sit together and the name keeps its width. */}
          <span className="flex items-center gap-1.5">
            <span className="nums font-mono text-2xs text-ink-faint">
              {timeOfDay(call.startedAt, tz)}
            </span>
            {/* The chip at its own size. `className="py-0 text-2xs"` was here and did
                NOTHING - `Chip`'s own `py-0.5` and `text-xs` are emitted later in the
                stylesheet and win, the same trap the pill controls in `ui.tsx` hit twice.
                A class that silently loses is worse than no class, because it reads as a
                decision somebody made. */}
            {call.mode !== "live" && <Chip>{call.mode}</Chip>}
            {/* WHO RANG WHOM, and it only appears when the answer is the unusual one. An
                "inbound" chip on every row would be noise on a log that is almost entirely
                inbound; a missing one on the handful of calls the agent placed itself
                would leave them indistinguishable from the calls it answered. Beside the
                mode rather than beside the handle, for the reason above. */}
            {call.direction === "outbound" && <Chip>outbound</Chip>}
          </span>
        </span>
      </span>

      {/* Length qualifies WHEN the call happened rather than standing on its own - nobody
          sorts a ledger by it - so it is set quiet and sits beside the caller. */}
      <span className="nums font-mono text-xs whitespace-nowrap text-ink-faint max-[720px]:col-start-2 max-[720px]:row-start-1 max-[720px]:text-right">
        {duration(call.durationMs)}
      </span>

      <span className="max-[720px]:col-start-1 max-[720px]:row-start-3">
        <QualityInline qualification={call.analysis.qualification} labels={words} />
      </span>

      {/*
        THE COLUMN THAT ABSORBS THE WIDTH, and `min-w-0` is on the GRID ITEM rather than
        only on the spans inside it. A grid item defaults to `min-width: auto` and will not
        shrink below its content, so without this a long headline pushes the money and the
        handoff off the right-hand edge at every width, not just narrow ones.
      */}
      <span className="col-start-4 grid min-w-0 max-[720px]:col-start-1 max-[720px]:col-span-3 max-[720px]:row-start-2">
        {head ? (
          <span className="truncate font-[550] text-ink">{head}</span>
        ) : (
          <span className="truncate text-ink-faint">No summary yet</span>
        )}
        {tail && <span className="truncate text-sm text-ink-faint">{tail}</span>}
      </span>

      <span className="nums text-right font-mono text-sm whitespace-nowrap text-ink max-[720px]:col-start-2 max-[720px]:row-start-3">
        {outcome.value && (
          <>
            {money(outcome.value.minor, outcome.value.currency ?? currency ?? null)}
            {/* Never bare, and the rule survives every move this figure has made. A quote
                read out over the phone is not revenue, and a figure that mixes the two
                silently is a dashboard that lies about money. */}
            <Basis inline>{basisLabel(outcome.value.basis)}</Basis>
          </>
        )}
      </span>

      {/*
        WHERE THE CALL STANDS, and a person's mark beats everything the agent said. A call
        somebody has marked handled says so and by whom, and nothing else: the reason it
        needed them is history. An open one says "open", which replaced a bare "unresolved"
        that only ever covered one of the three reasons a call waits on somebody - an urgent
        call the agent did resolve was flagged nowhere on the log.

        The handoff still has to be SOMEWHERE: its filter is in the bar above, and a filter
        whose value appears nowhere on the log is a filter the reader cannot verify. So on an
        unhandled call it rides beside the flag rather than being replaced by it.
      */}
      <span className="flex min-w-0 flex-wrap items-center justify-end gap-1.5 max-[720px]:col-start-3 max-[720px]:row-start-1">
        {call.handled ? (
          <Chip className="max-w-full">
            <span className="min-w-0 truncate">handled{call.handled.by ? ` by ${call.handled.by}` : ""}</span>
          </Chip>
        ) : (
          <>
            {call.needsPerson && <Flag>open</Flag>}
            {outcome.handoff && outcome.handoff !== "none" && (
              <Chip>{humanise(outcome.handoff)}</Chip>
            )}
          </>
        )}
      </span>
    </div>
  );
}
